// components/MultiSelectDropdown.js
import React, { useState, useEffect, useRef } from 'react';
import './MultiSelectDropdown.css';

const MultiSelectDropdown = ({
    label,
    options = [],
    selectedValues = [],
    onChange,
    placeholder = "Select options",
    enableSearch = true,
    isCategoryTree = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && enableSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, enableSearch]);

    // Filter options based on search
    const filteredOptions = options.filter(option => {
        if (!searchTerm) return true;
        const name = option.name || option.label || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    // Toggle selection
    const toggleSelection = (value) => {
        const newSelected = selectedValues.includes(value)
            ? selectedValues.filter(v => v !== value)
            : [...selectedValues, value];
        onChange(newSelected);
    };

    // Remove selected item
    const removeSelection = (value) => {
        const newSelected = selectedValues.filter(v => v !== value);
        onChange(newSelected);
    };

    // Select all filtered options
    const selectAllFiltered = () => {
        const filteredIds = filteredOptions.map(option => option.value || option._id);
        const newSelected = [...new Set([...selectedValues, ...filteredIds])];
        onChange(newSelected);
    };

    // Clear all selections
    const clearAll = () => {
        onChange([]);
        setSearchTerm('');
    };

    // Get selected options
    const selectedOptions = options.filter(option =>
        selectedValues.includes(option.value || option._id)
    );

    return (
        <div className="multi-select-dropdown" ref={dropdownRef}>
            {label && <label className="form-label">{label}</label>}

            {/* Selected Values Display */}
            <div
                className={`dropdown-select ${isOpen ? 'open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="selected-values">
                    {selectedOptions.length === 0 ? (
                        <span className="placeholder">{placeholder}</span>
                    ) : (
                        <div className="selected-tags">
                            {selectedOptions.map(option => (
                                <span key={option.value || option._id} className="selected-tag">
                                    <span className="tag-label">
                                        {option.name || option.label}
                                    </span>
                                    <button
                                        type="button"
                                        className="tag-remove-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeSelection(option.value || option._id);
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="dropdown-arrow">
                    <i className={`bi bi-chevron-${isOpen ? 'up' : 'down'}`}></i>
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="dropdown-menu">
                    {/* Search Bar */}
                    {enableSearch && (
                        <div className="dropdown-search">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="form-control"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    {/* Options */}
                    <div className="dropdown-options">
                        {filteredOptions.length === 0 ? (
                            <div className="no-options">No options found</div>
                        ) : (
                            <div className="options-list">
                                {filteredOptions.map(option => {
                                    const isSelected = selectedValues.includes(option.value || option._id);
                                    const indent = isCategoryTree ? (option.level || 0) * 20 : 0;

                                    return (
                                        <div
                                            key={option.value || option._id}
                                            className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                                            onClick={() => toggleSelection(option.value || option._id)}
                                        >
                                            <div className="form-check">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={isSelected}
                                                    readOnly
                                                />
                                                <label className="form-check-label">
                                                    <div style={{ marginLeft: `${indent}px` }}>
                                                        {option.name || option.label}
                                                        {isCategoryTree && option.level > 0 && (
                                                            <span className="text-muted ms-1">↳</span>
                                                        )}
                                                    </div>
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer with Actions */}
                    <div className="dropdown-footer">
                        <div className="dropdown-actions">
                            <button
                                type="button"
                                className="btn btn-sm btn-link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    selectAllFiltered();
                                }}
                            >
                                Select All
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-link"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearAll();
                                }}
                            >
                                Clear All
                            </button>
                        </div>
                        <div className="selected-count">
                            {selectedValues.length} selected
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MultiSelectDropdown;